import { useSchemaEntry } from "@/components/SchemaGraph";

interface StructuredDataProps {
  type: "Organization" | "FAQPage" | "Article" | "WebSite" | "HowTo" | "BreadcrumbList" | "Service" | "LocalBusiness" | "ProfessionalService" | "ItemList";
  data: any;
}

export const StructuredData = ({ type, data }: StructuredDataProps) => {
  let structuredData = {};

  switch (type) {
    case "ItemList":
      structuredData = { "@type": "ItemList", ...data };
      break;
    case "Organization":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Organization",
        name: data.name,
        url: data.url,
        logo: data.logo,
        description: data.description,
        sameAs: data.socialProfiles,
        contactPoint: {
          "@type": "ContactPoint",
          email: data.email,
          contactType: "Customer Service",
        },
        // LLM-friendly: explicit service offerings
        knowsAbout: [
          "Link Building",
          "SEO",
          "Backlink Strategy",
          "Guest Posting",
          "Niche Edits",
          "Digital Marketing"
        ],
      };
      break;

    case "FAQPage":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: data.questions.map((q: any) => ({
          "@type": "Question",
          name: q.question,
          acceptedAnswer: {
            "@type": "Answer",
            text: q.answer,
          },
        })),
      };
      break;

    case "Article":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Article",
        headline: data.headline,
        description: data.description,
        image: data.image,
        datePublished: data.datePublished,
        dateModified: data.dateModified,
        author: {
          "@type": "Person",
          name: data.author,
        },
        publisher: {
          "@type": "Organization",
          name: "Linkzy",
          logo: {
            "@type": "ImageObject",
            url: "https://linkzy.io/linkzy-logo-hq.png",
          },
        },
        // LLM-friendly: explicit citations and sources
        citation: data.citations || [],
        keywords: data.keywords || [],
      };
      break;

    case "WebSite":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: data.name,
        url: data.url,
        potentialAction: {
          "@type": "SearchAction",
          target: {
            "@type": "EntryPoint",
            urlTemplate: `${data.url}/blog?q={search_term_string}`,
          },
          "query-input": "required name=search_term_string",
        },
      };
      break;

    case "HowTo":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: data.name,
        description: data.description,
        step: data.steps.map((step: any) => ({
          "@type": "HowToStep",
          name: step.name,
          text: step.text,
        })),
        totalTime: data.totalTime,
        estimatedCost: data.estimatedCost,
      };
      break;

    case "BreadcrumbList":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: data.items.map((item: any, index: number) => ({
          "@type": "ListItem",
          position: index + 1,
          name: item.name,
          item: item.url,
        })),
      };
      break;

    case "Service":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "Service",
        name: data.name,
        description: data.description,
        provider: {
          "@type": "Organization",
          name: "Linkzy",
          url: "https://linkzy.io",
        },
        serviceType: data.serviceType,
        areaServed: data.areaServed || "Worldwide",
        hasOfferCatalog: data.offers ? {
          "@type": "OfferCatalog",
          name: data.catalogName || "Link Building Services",
          itemListElement: data.offers.map((offer: any) => ({
            "@type": "Offer",
            name: offer.name,
            price: offer.price,
            priceCurrency: offer.currency || "USD",
            description: offer.description,
          })),
        } : undefined,
        // LLM-friendly: explicit features and benefits
        additionalProperty: data.features?.map((feature: string) => ({
          "@type": "PropertyValue",
          name: "Feature",
          value: feature,
        })) || [],
      };
      break;

    case "ProfessionalService":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "ProfessionalService",
        name: data.name,
        description: data.description,
        url: data.url,
        priceRange: data.priceRange,
        areaServed: "Worldwide",
        serviceOutput: data.serviceOutput,
        knowsAbout: data.expertise || [],
        // LLM-friendly: explicit methodology
        additionalProperty: [
          {
            "@type": "PropertyValue",
            name: "Methodology",
            value: data.methodology || "White-hat link building with manual outreach",
          },
          {
            "@type": "PropertyValue",
            name: "ExperienceYears",
            value: data.experienceYears || "10+",
          },
        ],
      };
      break;

    case "LocalBusiness":
      structuredData = {
        "@context": "https://schema.org",
        "@type": "LocalBusiness",
        name: data.name,
        description: data.description,
        url: data.url,
        email: data.email,
        priceRange: data.priceRange,
        areaServed: "Worldwide",
      };
      break;
  }

  useSchemaEntry({ node: structuredData });
  return null;
};
